import { useRef, useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * MeetingMode — record a long meeting (≤2 hr), Whisper-transcribe,
 * extract design-change action items, click [Apply] to feed each
 * back into the active prompt box.
 */
export default function MeetingMode({ open, onClose, onApplyItem }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [duration, setDuration] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);

  const start = async () => {
    setError(""); setItems([]); setTranscript(""); setDuration(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
        clearInterval(tickRef.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 5000) {
          setError("Meeting too short — record at least a few seconds");
          return;
        }
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "meeting.webm");
          fd.append("language", "en");
          const r1 = await api.post("/voice/transcribe", fd,
                { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
          setTranscript(r1.data.transcript);
          const r2 = await api.post("/voice/meeting/extract-actions",
                { transcript: r1.data.transcript });
          setItems(r2.data.items || []);
        } catch (e) {
          setError(e?.response?.data?.detail || String(e));
        }
        setProcessing(false);
      };
      rec.start(1000);
      recRef.current = rec;
      setRecording(true);
      tickRef.current = setInterval(() => setDuration(d => d + 1), 1000);
      // Auto-cap at 2 hours
      setTimeout(() => { if (recRef.current?.state === "recording") rec.stop(); },
            2 * 60 * 60 * 1000);
    } catch (e) {
      setError("Microphone access denied or unavailable");
    }
  };
  const stop = () => {
    if (recRef.current?.state === "recording") {
      try { recRef.current.stop(); } catch (_) { /* ok */ }
    }
    setRecording(false);
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (!open) return null;
  return (
    <div data-testid="meeting-mode" style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
        zIndex: 1000, display: "flex", alignItems: "center",
        justifyContent: "center", padding: 32 }}>
      <div style={{ background: "var(--pf-card)", border: "1px solid var(--pf-border)",
                          borderRadius: 8, padding: 24, maxWidth: 720, width: "100%",
                          maxHeight: "85vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#F26B1F" }}>
              Meeting Mode
            </div>
            <div style={{ fontSize: 11, color: "var(--pf-muted)", marginTop: 2 }}>
              Record a discussion. AI extracts design-change action items afterwards.
            </div>
          </div>
          <button data-testid="meeting-close" onClick={onClose} style={btnGhost}>
            ✕ Close
          </button>
        </div>

        <div style={{ marginTop: 20, padding: 24, textAlign: "center",
                            background: "var(--pf-bg)", borderRadius: 6,
                            border: "1px solid var(--pf-border)" }}>
          {processing ? (
            <div style={{ color: "#F59E0B", fontSize: 14 }}>
              <Loader2 size={32} style={{ animation: "spin 1s linear infinite" }} />
              <div style={{ marginTop: 8 }}>Transcribing &amp; extracting actions…</div>
            </div>
          ) : (
            <button data-testid={recording ? "meeting-stop" : "meeting-start"}
                onClick={recording ? stop : start}
                style={{
                  background: recording ? "#EF4444" : "#F26B1F",
                  color: "white", border: "none", padding: "12px 24px",
                  borderRadius: 24, fontSize: 14, fontWeight: 600,
                  cursor: "pointer", display: "inline-flex",
                  alignItems: "center", gap: 8,
                  animation: recording ? "rachana-mic-pulse 1s infinite ease-in-out" : "none",
                }}>
              {recording ? <MicOff size={18} /> : <Mic size={18} />}
              {recording ? `Stop recording · ${fmt(duration)}` : "Start meeting recording"}
            </button>
          )}
          {recording && (
            <div style={{ marginTop: 12, fontSize: 11, color: "var(--pf-muted)" }}>
              Recording … (auto-stops at 2 hours)
            </div>
          )}
        </div>

        {error && <div style={{ marginTop: 12, color: "#EF4444",
                                            fontSize: 11 }}>⚠ {error}</div>}

        {items.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#F26B1F" }}>
              Action items detected ({items.length})
            </div>
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column",
                                gap: 8 }}>
              {items.map((it, i) => (
                <div key={i} data-testid={`meeting-action-${i}`}
                      style={{ background: "var(--pf-bg)",
                                    border: "1px solid var(--pf-border)",
                                    borderRadius: 4, padding: 10,
                                    display: "flex", justifyContent: "space-between",
                                    alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12 }}>{it.corrected}</div>
                    <div style={{ fontSize: 9, color: "var(--pf-muted)",
                                          marginTop: 2 }}>
                      Intent: <span style={{ color: "#F26B1F" }}>{it.intent}</span>
                      {" · "}Confidence: {(it.confidence * 100).toFixed(0)}%
                    </div>
                  </div>
                  <button data-testid={`meeting-apply-${i}`}
                      onClick={() => onApplyItem?.(it.corrected)} style={btn}>
                    Apply
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {transcript && (
          <details style={{ marginTop: 12 }}>
            <summary style={{ cursor: "pointer", fontSize: 11,
                                      color: "var(--pf-muted)" }}>
              Full transcript ({transcript.length} chars)
            </summary>
            <div style={{ marginTop: 6, padding: 10, fontSize: 11,
                                background: "var(--pf-bg)",
                                borderRadius: 4, maxHeight: 200, overflow: "auto",
                                whiteSpace: "pre-wrap" }}>
              {transcript}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

const btn = { background: "#F26B1F", color: "white", border: "none",
                  padding: "5px 14px", borderRadius: 4, fontSize: 11,
                  cursor: "pointer" };
const btnGhost = { ...btn, background: "transparent",
                          color: "var(--pf-text)",
                          border: "1px solid var(--pf-border)" };
